import { Component } from 'preact';
import { CONTENT_BG_DEFAULT, MENU_BG } from '../lib/medievalTheme';
import { createPortal } from 'preact/compat';

const CARD_AUTOCOMPLETE_LIMIT = 8;

// Custom undo/redo history. Contenteditable's native stack is gone
// because we rebuild the DOM on every keystroke. We snapshot the
// source text + caret offset at meaningful boundaries; 100 entries
// is plenty for a notes/comments surface (more than this is fishing
// for buyer's remorse the user would rather just retype).
const MAX_UNDO_HISTORY = 100;
// A run of consecutive typing keystrokes coalesces into a single
// undo entry. Pausing for this long closes the run so the next
// keystroke starts a new entry. Matches what native textareas do.
const TYPING_IDLE_MS = 800;
// One pattern for any markdown link. We differentiate card-vs-web
// downstream by inspecting the URL prefix. Capturing the URL with
// no inner whitespace keeps `[foo](bar baz)` from accidentally being
// treated as a link.
const LINK_PATTERN = /\[([^\]\n]+)\]\(([^)\s]+)\)/;
const isCardLinkUrl = (url) => /^card:/.test(url);
const isWebLinkUrl = (url) => /^https?:\/\//i.test(url);

// Standalone element-name word. Fire / water / earth / air as a
// whole word (case-insensitive). Renders as an element-tinted span
// with the SVG token glyph as a ::before pseudo-element so the
// span's textContent stays equal to the original source word.
// caret math sees no length shift.
const ELEMENT_WORD_PATTERN = /\b(fire|water|earth|air)\b/i;
function normalizeElement(word) {
  const lower = word.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

// Bare URL detector. Picks up `https://example.com` typed/pasted
// directly into the notes without the markdown link syntax. We
// stop at whitespace, brackets, and quotes so the URL never bleeds
// into surrounding prose, then trim trailing sentence punctuation
// (".", ",", ";", ":", "!", "?") so "see https://x.com." doesn't
// glue the period onto the URL.
function detectBareUrl(text) {
  const match = text.match(/https?:\/\/[^\s\[\]()<>"']+/);
  if (!match) return null;
  let url = match[0];
  while (/[.,;:!?]$/.test(url) && url.length > 9) url = url.slice(0, -1);
  return { url, index: match.index };
}

function openExternalUrl(url) {
  if (typeof window === 'undefined') return;
  // Electron's setWindowOpenHandler intercepts target=_blank and
  // routes via shell.openExternal. Same path the support-link
  // button in the hub uses.
  window.open(url, '_blank', 'noopener,noreferrer');
}

// Caret-anchored "@query" / "#query" detector. `@` is a card, `#` is a
// player. The query allows letters, numbers, apostrophes, hyphens, and a
// single trailing word break so users can type multi-word card names
// ("Adept Illusionist") without the autocomplete closing on every space.
function detectAtQuery(text, offset) {
  let i = offset - 1;
  while (i >= 0) {
    const ch = text[i];
    if (ch === '@' || ch === '#') {
      const before = i === 0 ? null : text[i - 1];
      // Trigger when @ stands at the start of a word. Anything
      // non-alphanumeric counts as a boundary so the autocomplete
      // also fires inside **bold**, *italic*, parentheses, commas,
      // etc. ("user@example" stays inert because letters precede @.)
      if (before === null || !/[A-Za-z0-9]/.test(before)) {
        return { atIndex: i, kind: ch === '#' ? 'player' : 'card', query: text.slice(i + 1, offset) };
      }
      return null;
    }
    if (ch === '\n') return null;
    if (offset - i > 60) return null;
    i--;
  }
  return null;
}

// Usernames are single words ([A-Za-z][A-Za-z0-9_]{2,15}), so a space
// ends the query; the popover closes rather than matching across words.
function filterPlayers(players, query) {
  if (!Array.isArray(players)) return [];
  const q = String(query || '').trim().toLowerCase();
  if (/\s/.test(query || '')) return [];
  const seen = new Set();
  const prefix = [];
  const contains = [];
  for (const p of players) {
    if (!p?.id || !p?.name || seen.has(p.id)) continue;
    seen.add(p.id);
    const lower = p.name.toLowerCase();
    if (!q || lower.startsWith(q)) prefix.push(p);
    else if (lower.includes(q)) contains.push(p);
  }
  return [...prefix, ...contains].slice(0, CARD_AUTOCOMPLETE_LIMIT);
}

function filterCards(sorceryCards, query) {
  if (!Array.isArray(sorceryCards)) return [];
  const q = String(query || '').trim().toLowerCase();
  if (!q) {
    return sorceryCards
      .filter((c) => c.name && c.unique_id)
      .slice(0, CARD_AUTOCOMPLETE_LIMIT);
  }
  const prefix = [];
  const contains = [];
  for (const card of sorceryCards) {
    if (!card.name || !card.unique_id) continue;
    const lower = card.name.toLowerCase();
    if (lower.startsWith(q)) prefix.push(card);
    else if (lower.includes(q)) contains.push(card);
    if (prefix.length >= CARD_AUTOCOMPLETE_LIMIT) break;
  }
  return [...prefix, ...contains].slice(0, CARD_AUTOCOMPLETE_LIMIT);
}

// Read .app-canvas's viewport rect once per measurement so children
// can convert viewport-space DOMRects (everything getBoundingClientRect
// returns) into canvas-local coordinates. Position:fixed inside
// .app-canvas is scoped to it (transform: translateZ(0) makes it a
// containing block), so canvas-local is the space we have to use.
function getCanvasRect() {
  if (typeof document === 'undefined') {
    return { left: 0, top: 0, width: 1600, height: 900 };
  }
  const el = document.querySelector('.app-canvas');
  if (!el) {
    return { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight };
  }
  return el.getBoundingClientRect();
}

// Source-text length of a DOM node, treating card pills (which
// display only the card name) as their full `[Name](card:id)` source
// via the data-card-source attribute. Everything else falls back to
// textContent. Which IS the source for visible markers like ## / ** /
// `code` / etc. that we render in plain text.
function nodeSourceLength(node) {
  if (!node) return 0;
  if (node.nodeType === Node.TEXT_NODE) return (node.nodeValue || '').length;
  if (node.nodeType !== Node.ELEMENT_NODE) return 0;
  if (node.dataset && node.dataset.cardSource) return node.dataset.cardSource.length;
  let total = 0;
  for (const child of node.childNodes) total += nodeSourceLength(child);
  return total;
}

// Root children grouped into visual lines. renderContent emits one div
// per line, but Chromium drops that div when Backspace is pressed in an
// empty editor (or after select-all + delete), leaving a bare <br> or
// text node at the root. Any run of non-div root nodes is one line, so
// getPlainText and getCaretOffset agree with each other and with what
// the user sees.
function groupRootLines(children) {
  const lines = [];
  let run = null;
  for (const child of children) {
    if (child.nodeName === 'DIV') {
      run = null;
      lines.push([child]);
    } else {
      if (!run) { run = []; lines.push(run); }
      run.push(child);
    }
  }
  return lines;
}

/**
 * WYSIWYG markdown editor. A contenteditable that re-styles each line
 * in place as the user types. There's no preview pane: a line that
 * starts with "# " immediately renders as a heading, "- " becomes a
 * bullet, **foo** shows up bold, etc. Markdown markers stay visible
 * (in a muted color) so the user can see what they typed.
 *
 * Card links are special: `[Name](card:unique_id)` renders as an
 * atomic non-editable pill that displays just `@Name`. The bracket
 * + URL syntax never reaches the user's eyes. The pill stores the
 * full source on a data attribute so getPlainText still round-trips
 * the canonical markdown back to the parent.
 *
 * The contenteditable div is rendered with no children. Its inner
 * DOM is built imperatively via createElement / appendChild so we
 * never touch innerHTML and Preact reconciliation never stomps on
 * the contents.
 */
export default class MarkdownEditor extends Component {
  constructor(props) {
    super(props);
    this.editorRef = null;
    this._lastValue = props.value || '';
    this._isFocused = false;
    // Undo history. See header constants. Each entry is a
    // { text, caret } snapshot of the editor source. Snapshots
    // are pushed *before* the change they precede, so a single
    // `undo()` restores the state the user remembers as "what I
    // had before that last keystroke run / pill insert / etc."
    this._undoStack = [];
    this._redoStack = [];
    this._typingActive = false;
    this._typingMode = null;
    this._typingTimer = null;
    this.state = {
      autocomplete: null,
    };
  }

  componentDidMount() {
    this.renderContent(this._lastValue);
    if (this.props.autoFocus) this.focusEditor();
  }

  // Focus with the caret at the end. `focusKey` lets a parent ask for
  // this after mount: every change of the key focuses once.
  focusEditor() {
    requestAnimationFrame(() => {
      if (!this.editorRef) return;
      this.editorRef.focus();
      this.placeCaretAtEnd();
      // If the existing content already overflows the editor, the
      // caret lands past the visible scroll area on first focus.
      // scroll it back into view so the user can see where they're
      // about to type.
      this.ensureCaretVisible();
    });
  }

  componentDidUpdate(prevProps) {
    if (this.props.focusKey && this.props.focusKey !== prevProps.focusKey) this.focusEditor();
    if (this.props.value !== prevProps.value && this.props.value !== this._lastValue) {
      this._lastValue = this.props.value || '';
      this.renderContent(this._lastValue);
      // Rebuilding the lines drops the selection onto the editor root.
      // Put it back on a real line so the caret overlay measures a spot
      // inside the editor, not wherever the root's collapsed rect lands.
      if (document.activeElement === this.editorRef) this.placeCaretAtEnd();
      // Parent-driven value change (e.g. comment composer clearing
      // its draft after a successful post). Our prior undo history
      // is no longer meaningful against the new baseline.
      this._undoStack = [];
      this._redoStack = [];
      this._endTypingSession();
    }
  }

  componentWillUnmount() {
    if (this._typingTimer) clearTimeout(this._typingTimer);
  }

  // ── Undo / redo ──────────────────────────────────────────

  pushHistorySnapshot() {
    this._undoStack.push({ text: this._lastValue, caret: this.getCaretOffset() });
    if (this._undoStack.length > MAX_UNDO_HISTORY) this._undoStack.shift();
    // Any new edit invalidates the redo branch. Same as every
    // mainstream text editor.
    this._redoStack.length = 0;
  }

  beginTypingSession(mode) {
    // Switching between insert and delete in the middle of a session
    // splits the entry. Otherwise one Cmd+Z would walk back both
    // the typing AND the subsequent backspacing in a single step.
    if (this._typingActive && this._typingMode !== mode) {
      this._endTypingSession();
    }
    if (!this._typingActive) {
      this.pushHistorySnapshot();
      this._typingActive = true;
      this._typingMode = mode;
    }
    this._bumpTypingTimer();
  }

  _bumpTypingTimer() {
    if (this._typingTimer) clearTimeout(this._typingTimer);
    this._typingTimer = setTimeout(() => this._endTypingSession(), TYPING_IDLE_MS);
  }

  _endTypingSession() {
    this._typingActive = false;
    this._typingMode = null;
    if (this._typingTimer) {
      clearTimeout(this._typingTimer);
      this._typingTimer = null;
    }
  }

  pushStructuralSnapshot() {
    // For non-typing edits (pill insertion, list auto-continue,
    // Cmd+B/I wrap). Always closes any open typing session. These
    // operations are conceptually a separate action than the
    // characters typed before them.
    this._endTypingSession();
    this.pushHistorySnapshot();
  }

  undo = () => {
    this._endTypingSession();
    if (this._undoStack.length === 0) return;
    const current = { text: this._lastValue, caret: this.getCaretOffset() };
    const prev = this._undoStack.pop();
    this._redoStack.push(current);
    this._applySnapshot(prev);
  };

  redo = () => {
    this._endTypingSession();
    if (this._redoStack.length === 0) return;
    const current = { text: this._lastValue, caret: this.getCaretOffset() };
    const next = this._redoStack.pop();
    this._undoStack.push(current);
    this._applySnapshot(next);
  };

  _applySnapshot({ text, caret }) {
    this._lastValue = text;
    this.renderContent(text);
    this.setCaretOffset(caret);
    if (this.props.onChange) this.props.onChange(text);
    this.updateAutocomplete();
    this.ensureCaretVisible();
  }

  // ── DOM ↔ source-text helpers ────────────────────────────
  //
  // Source-text view of the editor: each line div translates to one
  // line, joined by "\n." Card pills contribute their data-card-source
  // (the full markdown link) rather than their visible text.

  getPlainText() {
    if (!this.editorRef) return '';
    return this.sourceOfRootNodes(this.editorRef.childNodes);
  }

  sourceOfRootNodes(children) {
    return groupRootLines(children)
      .map((nodes) => nodes.map((n) => this.collectLineSource(n)).join(''))
      .join('\n');
  }

  hasLineStructure() {
    const children = this.editorRef.childNodes;
    return children.length > 0 && Array.from(children).every((n) => n.nodeName === 'DIV');
  }

  collectLineSource(node) {
    if (node.nodeType === Node.TEXT_NODE) return node.nodeValue || '';
    if (node.nodeType !== Node.ELEMENT_NODE) return '';
    if (node.dataset && node.dataset.cardSource) return node.dataset.cardSource;
    let out = '';
    for (const child of node.childNodes) out += this.collectLineSource(child);
    return out;
  }

  // Caret position as a source-text offset. Walks line divs first,
  // then drops into the target line and counts source-length of each
  // sibling preceding the range endpoint.
  getCaretOffset() {
    if (!this.editorRef) return 0;
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return 0;
    const range = sel.getRangeAt(0);
    const { endContainer: container, endOffset } = range;
    if (!this.editorRef.contains(container)) return 0;
    if (container === this.editorRef) {
      // Caret parked between root children (Chromium does this after
      // unwrapping the line div): everything before it is the offset.
      return this.sourceOfRootNodes(Array.from(this.editorRef.childNodes).slice(0, endOffset)).length;
    }

    let offset = 0;
    for (const nodes of groupRootLines(this.editorRef.childNodes)) {
      if (nodes.some((n) => n === container || (n.nodeType === Node.ELEMENT_NODE && n.contains(container)))) {
        return offset + this.offsetWithinNodes(nodes, container, endOffset);
      }
      offset += nodes.reduce((len, n) => len + nodeSourceLength(n), 0) + 1;
    }
    return Math.max(0, offset - 1);
  }

  offsetWithin(parent, container, containerOffset) {
    if (parent === container) {
      let count = 0;
      for (let i = 0; i < containerOffset; i++) {
        count += nodeSourceLength(parent.childNodes[i]);
      }
      return count;
    }
    return this.offsetWithinNodes(parent.childNodes, container, containerOffset);
  }

  offsetWithinNodes(nodes, container, containerOffset) {
    let count = 0;
    for (const child of nodes) {
      if (child === container) {
        if (child.nodeType === Node.TEXT_NODE) return count + containerOffset;
        if (child.dataset && child.dataset.cardSource) return count + child.dataset.cardSource.length;
        return count + this.offsetWithin(child, container, containerOffset);
      }
      if (child.nodeType === Node.ELEMENT_NODE && child.contains(container)) {
        if (child.dataset && child.dataset.cardSource) {
          // Cursor shouldn't normally land inside a pill (it's
          // contenteditable=false). But if a stray click does, treat
          // the position as the end of the pill source.
          return count + child.dataset.cardSource.length;
        }
        return count + this.offsetWithin(child, container, containerOffset);
      }
      count += nodeSourceLength(child);
    }
    return count;
  }

  setCaretOffset(target) {
    if (!this.editorRef) return;
    let remaining = Math.max(0, target);
    for (const lineDiv of this.editorRef.childNodes) {
      if (lineDiv.nodeType !== Node.ELEMENT_NODE) continue;
      const lineLen = nodeSourceLength(lineDiv);
      if (remaining <= lineLen) {
        this.placeCaretWithin(lineDiv, remaining);
        return;
      }
      remaining -= lineLen + 1;
    }
    this.placeCaretAtEnd();
  }

  placeCaretWithin(parent, sourceOffset) {
    const sel = window.getSelection();
    const range = document.createRange();
    let remaining = sourceOffset;
    for (let i = 0; i < parent.childNodes.length; i++) {
      const child = parent.childNodes[i];
      // The <br> that props an empty line open (or ends a line). A caret
      // collapsed INSIDE or AFTER a trailing <br> is never painted by
      // Chromium, so land before it, as a child offset of the line.
      if (child.nodeName === 'BR') {
        range.setStart(parent, i);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
        return;
      }
      const len = nodeSourceLength(child);
      if (remaining <= len) {
        if (child.nodeType === Node.TEXT_NODE) {
          range.setStart(child, remaining);
          range.collapse(true);
          sel.removeAllRanges();
          sel.addRange(range);
          return;
        }
        if (child.nodeType === Node.ELEMENT_NODE && child.dataset && child.dataset.cardSource) {
          // Place the caret immediately before the pill when the
          // offset is at its start, after when at its end.
          if (remaining === 0) {
            range.setStartBefore(child);
          } else {
            range.setStartAfter(child);
          }
          range.collapse(true);
          sel.removeAllRanges();
          sel.addRange(range);
          return;
        }
        if (child.nodeType === Node.ELEMENT_NODE) {
          this.placeCaretWithin(child, remaining);
          return;
        }
      }
      remaining -= len;
    }
    range.selectNodeContents(parent);
    range.collapse(false);
    sel.removeAllRanges();
    sel.addRange(range);
  }

  // End of the LAST LINE, never the editor root: a caret collapsed after
  // the last line div sits at the root's block level, which Chromium
  // paints as a tall bar hanging below the first row.
  placeCaretAtEnd() {
    if (!this.editorRef) return;
    let last = null;
    for (const child of this.editorRef.childNodes) {
      if (child.nodeType === Node.ELEMENT_NODE) last = child;
    }
    if (last) {
      this.placeCaretWithin(last, nodeSourceLength(last));
      return;
    }
    const range = document.createRange();
    range.selectNodeContents(this.editorRef);
    range.collapse(false);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
  }

  // Scroll the editor so the caret line stays inside the visible
  // scroll area. The native contenteditable auto-scrolls on text
  // input, but it does NOT scroll when the caret lands on a brand-
  // new empty line (e.g. Enter at the very end of content) or when
  // the caret is moved programmatically via setCaretOffset /
  // placeCaretAtEnd. We also can't rely on the browser's caret
  // scroll target because we render a custom caret overlay on top
  // The native caret is hidden, so Chromium has nothing to keep
  // in view. This method is the manual equivalent.
  ensureCaretVisible() {
    if (!this.editorRef) return;
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return;
    const range = sel.getRangeAt(0).cloneRange();
    range.collapse(false);

    // For a caret on an empty line, range.getBoundingClientRect()
    // collapses to a zero-height rect at the line's start. Usable,
    // but if the container is a block with display:flex we can get
    // a degenerate rect at the editor's top-left. Fall back to the
    // line element's rect so the math still tracks the actual line.
    let caretRect = range.getBoundingClientRect();
    if (caretRect.height === 0) {
      const node = range.endContainer.nodeType === Node.ELEMENT_NODE
        ? range.endContainer
        : range.endContainer.parentElement;
      if (node && node.getBoundingClientRect) {
        caretRect = node.getBoundingClientRect();
      }
    }

    const editorRect = this.editorRef.getBoundingClientRect();
    const pad = 4;
    if (caretRect.bottom > editorRect.bottom - pad) {
      this.editorRef.scrollTop += caretRect.bottom - editorRect.bottom + pad;
    } else if (caretRect.top < editorRect.top + pad) {
      this.editorRef.scrollTop -= editorRect.top - caretRect.top + pad;
    }
  }

  // Caret position in CANVAS-LOCAL coordinates. Used by the
  // autocomplete popover and the hover preview. Viewport coords from
  // getBoundingClientRect won't work because position:fixed inside
  // .app-canvas is scoped to its containing block.
  getCaretCanvasRect() {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return null;
    const range = sel.getRangeAt(0).cloneRange();
    range.collapse(false);
    const rects = range.getClientRects();
    let viewportRect = rects.length > 0 ? rects[rects.length - 1] : null;
    if (!viewportRect) {
      const node = range.endContainer.nodeType === Node.ELEMENT_NODE
        ? range.endContainer
        : range.endContainer.parentElement;
      if (!node || !node.getBoundingClientRect) return null;
      viewportRect = node.getBoundingClientRect();
    }
    const canvas = getCanvasRect();
    return {
      left: viewportRect.left - canvas.left,
      right: viewportRect.right - canvas.left,
      top: viewportRect.top - canvas.top,
      bottom: viewportRect.bottom - canvas.top,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
    };
  }

  syncFromDom = () => {
    const text = this.getPlainText();
    const offset = this.getCaretOffset();
    if (text === this._lastValue) {
      // Backspace in an empty editor leaves the root without a line
      // div (see groupRootLines). Rebuild so the next keystroke lands
      // inside a line rather than as a bare root text node.
      if (!this.hasLineStructure()) {
        this.renderContent(text);
        this.setCaretOffset(offset);
      }
      this.updateAutocomplete();
      return;
    }
    // Treat any non-shrinking change as 'insert', everything else as
    // 'delete'. The mode is used to split typing↔backspacing into
    // separate undo entries; we also break the session on word
    // boundaries (whitespace, punctuation) so Cmd+Z walks the
    // user's text back word-by-word like a native input.
    const mode = text.length >= this._lastValue.length ? 'insert' : 'delete';
    this.beginTypingSession(mode);

    this._lastValue = text;
    this.renderContent(text);
    this.setCaretOffset(offset);
    if (this.props.onChange) this.props.onChange(text);
    this.updateAutocomplete();
    // Chromium auto-scrolls a contenteditable on text insertion, but
    // *not* when the change is an Enter that opens a new empty line
    // past the visible area. And our custom caret overlay means the
    // browser has nothing to chase either. Force the scroll target
    // here so the caret never strays below the viewport edge.
    this.ensureCaretVisible();

    if (mode === 'insert' && offset > 0) {
      const lastChar = text[offset - 1] || '';
      if (/[\s.,;:!?]/.test(lastChar)) {
        this._endTypingSession();
      }
    }
  };

  updateAutocomplete = () => {
    const text = this._lastValue;
    const offset = this.getCaretOffset();
    const match = detectAtQuery(text, offset);
    const source = match?.kind === 'player' ? this.props.mentionPlayers : this.props.sorceryCards;
    if (!match || !source) {
      if (this.state.autocomplete) this.setState({ autocomplete: null });
      return;
    }
    const results = match.kind === 'player'
      ? filterPlayers(source, match.query)
      : filterCards(source, match.query);
    if (results.length === 0) {
      if (this.state.autocomplete) this.setState({ autocomplete: null });
      return;
    }
    const rect = this.getCaretCanvasRect();
    const prev = this.state.autocomplete;
    // Stickiness: once a popover opens for a given @-mention (keyed
    // by the position of the `@` in the text), keep the chosen side
    // for the whole session even as the user types and the result
    // count changes. Otherwise the popover flips below↔above on
    // every keystroke that crosses the "fits naturally" threshold.
    // disorienting, and the new side is usually the cramped one.
    // The side resets when the user deletes back past the `@` and
    // starts a new mention, or when the popover closes outright.
    const placeAbove = prev && prev.atIndex === match.atIndex && rect
      ? prev.placeAbove
      : (rect ? this.choosePopoverSide(rect, results.length) : false);
    this.setState({
      autocomplete: {
        atIndex: match.atIndex,
        kind: match.kind,
        queryEnd: offset,
        query: match.query,
        results,
        selected: 0,
        anchor: rect,
        placeAbove,
      },
    });
  };

  // Decide which side of the caret the autocomplete popover should
  // open on. Stay below if the natural-size popover fits there;
  // otherwise flip to whichever side has more room. Called once per
  // mention session (see updateAutocomplete) so the choice is
  // sticky.
  choosePopoverSide(anchor, resultCount) {
    const { canvasWidth, canvasHeight } = anchor;
    const cqw = canvasWidth / 100;
    const cqh = canvasHeight / 100;
    // Must mirror renderAutocomplete's row/padding/cap values so the
    // side decision aligns with what'll actually render.
    const rowH = 2.75 * cqw;
    const listPadding = 0.5 * cqw;
    const listCssCap = 22 * cqh;
    const visibleCount = Math.min(resultCount, CARD_AUTOCOMPLETE_LIMIT);
    const naturalH = Math.min(visibleCount * rowH + listPadding, listCssCap);
    const gap = 0.4 * cqw;
    const edgePad = 0.5 * cqw;
    const spaceBelow = canvasHeight - anchor.bottom - edgePad - gap;
    const spaceAbove = anchor.top - edgePad - gap;
    return spaceBelow < naturalH && spaceAbove > spaceBelow;
  }

  handleInput = () => {
    this.syncFromDom();
  };

  handleKeyDown = (e) => {
    const mod = e.metaKey || e.ctrlKey;

    // Undo / redo. Checked first so Cmd+Z works even while the
    // autocomplete popover is open. Cmd+Z = undo; Cmd+Shift+Z and
    // Ctrl+Y both redo, mirroring the macOS and Windows conventions.
    if (mod && (e.key === 'z' || e.key === 'Z')) {
      e.preventDefault();
      if (e.shiftKey) this.redo();
      else this.undo();
      return;
    }
    if (mod && !e.shiftKey && (e.key === 'y' || e.key === 'Y')) {
      e.preventDefault();
      this.redo();
      return;
    }

    const { autocomplete } = this.state;

    if (autocomplete && autocomplete.results.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.setState({
          autocomplete: {
            ...autocomplete,
            selected: (autocomplete.selected + 1) % autocomplete.results.length,
          },
        });
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.setState({
          autocomplete: {
            ...autocomplete,
            selected: (autocomplete.selected - 1 + autocomplete.results.length) % autocomplete.results.length,
          },
        });
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        const card = autocomplete.results[autocomplete.selected];
        if (card) this.insertCardLink(card);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        this.setState({ autocomplete: null });
        return;
      }
    }

    if (mod && (e.key === 'b' || e.key === 'B')) {
      e.preventDefault();
      this.wrapSelection('**', '**');
      return;
    }
    if (mod && (e.key === 'i' || e.key === 'I')) {
      e.preventDefault();
      this.wrapSelection('*', '*');
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      const offset = this.getCaretOffset();
      const text = this.getPlainText();
      const lineStart = text.lastIndexOf('\n', offset - 1) + 1;
      const currentLine = text.slice(lineStart, offset);

      const bulletMatch = currentLine.match(/^([-*])\s+(.*)$/);
      if (bulletMatch) {
        e.preventDefault();
        const [, marker, content] = bulletMatch;
        if (!content.trim()) {
          const next = text.slice(0, lineStart) + text.slice(offset);
          this.replaceAll(next, lineStart);
          return;
        }
        const insertion = `\n${marker} `;
        const next = text.slice(0, offset) + insertion + text.slice(offset);
        this.replaceAll(next, offset + insertion.length);
        return;
      }

      const orderedMatch = currentLine.match(/^(\d+)\.\s+(.*)$/);
      if (orderedMatch) {
        e.preventDefault();
        const [, num, content] = orderedMatch;
        if (!content.trim()) {
          const next = text.slice(0, lineStart) + text.slice(offset);
          this.replaceAll(next, lineStart);
          return;
        }
        const nextNum = parseInt(num, 10) + 1;
        const insertion = `\n${nextNum}. `;
        const next = text.slice(0, offset) + insertion + text.slice(offset);
        this.replaceAll(next, offset + insertion.length);
        return;
      }

      // No list context. If a parent registered onSubmit (e.g. the
      // chat-style comment composer), Enter posts the message and
      // Shift+Enter falls through to the default newline.
      if (this.props.onSubmit) {
        e.preventDefault();
        this.props.onSubmit();
      }
    }
  };

  wrapSelection(prefix, suffix) {
    if (!this.editorRef) return;
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return;
    const text = this.getPlainText();
    const range = sel.getRangeAt(0);

    const start = this.rangeToOffset(range.startContainer, range.startOffset);
    const end = this.rangeToOffset(range.endContainer, range.endOffset);
    if (start == null || end == null) return;
    const lo = Math.min(start, end);
    const hi = Math.max(start, end);
    const selected = text.slice(lo, hi);
    const insertion = `${prefix}${selected}${suffix}`;
    const next = text.slice(0, lo) + insertion + text.slice(hi);
    const caret = selected ? lo + insertion.length : lo + prefix.length;
    this.replaceAll(next, caret);
  }

  rangeToOffset(container, offsetInNode) {
    if (!this.editorRef.contains(container)) return null;
    let offset = 0;
    for (const lineDiv of this.editorRef.childNodes) {
      if (lineDiv.nodeType !== Node.ELEMENT_NODE) continue;
      if (lineDiv === container || lineDiv.contains(container)) {
        return offset + this.offsetWithin(lineDiv, container, offsetInNode);
      }
      offset += nodeSourceLength(lineDiv) + 1;
    }
    return offset;
  }

  insertCardLink = (entry) => {
    const { autocomplete } = this.state;
    if (!autocomplete || !entry) return;
    const text = this.getPlainText();
    const queryEnd = this.getCaretOffset();
    // Players stay as plain `#Name`; the chat server resolves the name
    // and the renderer turns it into a profile pill.
    const linkText = autocomplete.kind === 'player'
      ? `#${entry.name} `
      : `[${entry.name}](card:${entry.unique_id}) `;
    const next = text.slice(0, autocomplete.atIndex) + linkText + text.slice(queryEnd);
    this.setState({ autocomplete: null });
    this.replaceAll(next, autocomplete.atIndex + linkText.length);
  };

  replaceAll(text, caretOffset) {
    // Every callsite (wrapSelection, insertCardLink, list auto-continue
    // in handleKeyDown) is a structural mutation. Record an undo
    // snapshot here once instead of duplicating the call at each
    // site.
    this.pushStructuralSnapshot();
    this._lastValue = text;
    this.renderContent(text);
    this.setCaretOffset(caretOffset);
    if (this.props.onChange) this.props.onChange(text);
    this.ensureCaretVisible();
  }

  renderContent(text) {
    const editor = this.editorRef;
    if (!editor) return;
    while (editor.firstChild) editor.removeChild(editor.firstChild);

    const handlers = {
      onCardClick: this.handleCardLinkClick,
      onCardHover: this.handleCardLinkHover,
      onCardHoverEnd: this.handleCardLinkHoverEnd,
    };

    const lines = text.split('\n');
    for (const line of lines) {
      editor.appendChild(buildLineNode(line, handlers));
    }
    if (lines.length === 0) {
      editor.appendChild(buildLineNode('', handlers));
    }
  }

  handleCardLinkClick = (cardId, event) => {
    event.preventDefault();
    event.stopPropagation();
    if (this.props.onOpenCard) this.props.onOpenCard(cardId);
  };

  handleCardLinkHover = (cardId, element) => {
    if (this.props.onCardLinkHover) this.props.onCardLinkHover(cardId, element);
  };

  handleCardLinkHoverEnd = () => {
    if (this.props.onCardLinkHoverEnd) this.props.onCardLinkHoverEnd();
  };

  renderAutocomplete() {
    const { autocomplete } = this.state;
    if (!autocomplete || !autocomplete.anchor) return null;
    const { results, selected, anchor, placeAbove } = autocomplete;
    const { canvasWidth, canvasHeight } = anchor;

    const cqw = canvasWidth / 100;
    const cqh = canvasHeight / 100;
    // Row height matches the actual rendered .md-autocomplete-item:
    // 1.95cqw thumb + 0.35cqw padding on each side (= 2.65cqw) plus
    // the 0.1cqw inter-item margin from `.item + .item { margin-top }`.
    // Earlier this was 2.5cqw, which underestimated the row by ~0.25cqw
    // and clipped the bottom of a single-result popover.
    const rowH = 2.75 * cqw;
    // .md-autocomplete-list has 0.25cqw padding top + bottom.
    const listPadding = 0.5 * cqw;
    // .md-autocomplete-list caps at max-height: 22cqh in CSS. Mirror
    // it so the placement math reflects what actually renders.
    const listCssCap = 22 * cqh;
    const visibleCount = Math.min(results.length, CARD_AUTOCOMPLETE_LIMIT);
    const naturalH = Math.min(
      visibleCount * rowH + listPadding,
      listCssCap,
    );

    // Gap between caret line and popover, edge padding from canvas
    // sides. Available space on each side of the caret.
    const gap = 0.4 * cqw;
    const edgePad = 0.5 * cqw;
    const spaceBelow = canvasHeight - anchor.bottom - edgePad - gap;
    const spaceAbove = anchor.top - edgePad - gap;

    // `placeAbove` is decided once per @-session in updateAutocomplete
    // and frozen on the state. This method just consumes it so the
    // popover never flips sides as the user types and the row count
    // shifts. Anchor is always relative to the live caret, height
    // shrinks to fit; position is never clamped to a canvas edge.
    // Floor at "one full row + list padding" so the visible entry is
    // never clipped. If the side genuinely lacks even that much
    // space, we'd rather overflow the canvas a hair than chop the
    // only thing the popover is meant to show.
    const sideSpace = placeAbove ? spaceAbove : spaceBelow;
    const popoverH = Math.max(
      rowH + listPadding,
      Math.min(naturalH, sideSpace),
    );

    const popoverWidthCqw = 20;
    const popoverWidthPx = popoverWidthCqw * cqw;
    const left = Math.max(edgePad, Math.min(anchor.left, canvasWidth - popoverWidthPx - edgePad));
    const top = placeAbove
      ? anchor.top - popoverH - gap
      : anchor.bottom + gap;

    // The popover root has a 1px border on both sides. With Tailwind's
    // global box-sizing: border-box, its inner content area is
    // (popoverH - 2px), so the list. Which also lives in border-box
    // space. Has to be capped to that inner height. Otherwise its
    // bottom 2px (part of the 0.25cqw bottom padding) gets clipped
    // by the popover's overflow:hidden, and the last item ends up
    // visually tighter against the bottom edge than the first item
    // does against the top.
    const POPOVER_BORDER_PX = 1;
    const listMaxH = popoverH - 2 * POPOVER_BORDER_PX;

    const node = (
      <div
        className="md-autocomplete"
        style={{
          position: 'fixed',
          top,
          left,
          width: `${popoverWidthCqw}cqw`,
          maxHeight: `${popoverH}px`,
          zIndex: 260,
          // Same carved-stone stack as CardMentionPopover (single-sourced
          // in medievalTheme); without it the rim floats over a see-through
          // list. `.md-autocomplete` carries an opaque CSS fallback too.
          background: `${CONTENT_BG_DEFAULT}, ${MENU_BG}`,
          backgroundBlendMode: 'overlay, normal',
        }}
        onMouseDown={(e) => e.preventDefault()}
      >
        <div className="md-autocomplete-list" style={{ maxHeight: `${listMaxH}px` }}>
          {results.map((card, idx) => {
            const printing = card.printings?.[0];
            const isActive = idx === selected;
            if (autocomplete.kind === 'player') {
              return (
                <button
                  key={card.id}
                  type="button"
                  className={`md-autocomplete-item ${isActive ? 'is-active' : ''}`}
                  onMouseEnter={() => this.setState({ autocomplete: { ...autocomplete, selected: idx } })}
                  onClick={() => this.insertCardLink(card)}
                >
                  {card.avatarUrl ? (
                    <img className="md-autocomplete-thumb" src={card.avatarUrl} alt="" draggable={false} style={{ borderRadius: '50%', objectFit: 'cover' }} />
                  ) : (
                    <span className="md-autocomplete-thumb md-autocomplete-thumb-fallback">{card.name.slice(0, 1).toUpperCase()}</span>
                  )}
                  <span className="md-autocomplete-label">
                    <span className="md-autocomplete-name">{card.name}</span>
                    {card.meta ? <span className="md-autocomplete-meta">{card.meta}</span> : null}
                  </span>
                </button>
              );
            }
            return (
              <button
                key={card.unique_id}
                type="button"
                className={`md-autocomplete-item ${isActive ? 'is-active' : ''}`}
                onMouseEnter={() => this.setState({ autocomplete: { ...autocomplete, selected: idx } })}
                onClick={() => this.insertCardLink(card)}
              >
                {printing?.image_url ? (
                  <img className="md-autocomplete-thumb" src={printing.image_url} alt="" draggable={false} />
                ) : (
                  <span className="md-autocomplete-thumb md-autocomplete-thumb-fallback">◆</span>
                )}
                <span className="md-autocomplete-label">
                  <span className="md-autocomplete-name">{card.name}</span>
                  {card.type ? <span className="md-autocomplete-meta">{card.type}</span> : null}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );

    // Portal to .app-canvas: the notes panel applies a transform
    // (for the entry animation) which makes it a containing block
    // for any position:fixed descendant, so rendering the popover
    // in-tree placed it relative to the panel, not the caret. The
    // canvas itself has container-type: size + transform, so portaling
    // there keeps cqw units calibrated to canvas dims AND scopes
    // position:fixed to the canvas. Which is the space our anchor
    // coords are computed in.
    const canvas = typeof document !== 'undefined' ? document.querySelector('.app-canvas') : null;
    return canvas ? createPortal(node, canvas) : node;
  }

  render() {
    const empty = !this._lastValue;
    return (
      <div className="relative flex-1 min-h-0">
        <div
          ref={(el) => { this.editorRef = el; }}
          contentEditable="true"
          onInput={this.handleInput}
          onKeyDown={this.handleKeyDown}
          onFocus={() => { this._isFocused = true; this.forceUpdate(); }}
          onBlur={() => { this._isFocused = false; this.forceUpdate(); }}
          spellcheck={false}
          suppressContentEditableWarning
          className={`md-editor h-full overflow-y-auto outline-none text-xs leading-relaxed pr-2 -mr-2 ${this.props.className || ''}`}
          style={{
            ...this.props.style,
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
          }}
        />
        {empty && !this._isFocused && this.props.placeholder && (
          <div
            className="absolute top-0 left-0 right-0 pointer-events-none text-xs leading-relaxed italic whitespace-pre-wrap"
            style={{ color: 'rgba(166, 160, 155, 0.4)' }}
          >
            {this.props.placeholder}
          </div>
        )}
        {this.renderAutocomplete()}
      </div>
    );
  }
}

// ────────────────────────── DOM builders ──────────────────────────

function buildLineNode(line, handlers) {
  const div = document.createElement('div');

  if (line === '') {
    div.className = 'md-line md-paragraph';
    div.appendChild(document.createElement('br'));
    return div;
  }

  const heading = line.match(/^(#{1,3})(\s+)(.*)$/);
  if (heading) {
    const level = heading[1].length;
    div.className = `md-line md-h${level}`;
    div.appendChild(buildMarker(`${heading[1]}${heading[2]}`));
    appendInline(div, heading[3], handlers);
    return div;
  }

  const bullet = line.match(/^([-*])(\s+)(.*)$/);
  if (bullet) {
    div.className = 'md-line md-bullet';
    div.appendChild(buildMarker(`${bullet[1]}${bullet[2]}`, 'md-bullet-marker'));
    appendInline(div, bullet[3], handlers);
    return div;
  }

  const ordered = line.match(/^(\d+\.)(\s+)(.*)$/);
  if (ordered) {
    div.className = 'md-line md-ol';
    div.appendChild(buildMarker(`${ordered[1]}${ordered[2]}`, 'md-ol-marker'));
    appendInline(div, ordered[3], handlers);
    return div;
  }

  div.className = 'md-line md-paragraph';
  appendInline(div, line, handlers);
  return div;
}

function buildMarker(text, extraClass) {
  const span = document.createElement('span');
  span.className = extraClass ? `md-marker ${extraClass}` : 'md-marker';
  span.textContent = text;
  return span;
}

// Build an atomic card-link pill. The pill is contenteditable=false
// so the cursor treats it as a single character. Arrow keys jump
// past it, backspace right after it deletes it cleanly, and the
// brackets+URL of the markdown source never bleed into the visible
// glyphs. The full source is stored on data-card-source so the
// editor can round-trip back to canonical markdown via getPlainText.
function buildCardLink(name, cardId, handlers) {
  const span = document.createElement('span');
  span.className = 'md-card-link';
  span.contentEditable = 'false';
  span.dataset.cardId = cardId;
  span.dataset.cardSource = `[${name}](card:${cardId})`;
  span.textContent = name;

  if (handlers?.onCardClick) {
    span.addEventListener('click', (e) => handlers.onCardClick(cardId, e));
  }
  if (handlers?.onCardHover) {
    span.addEventListener('mouseenter', () => handlers.onCardHover(cardId, span));
  }
  if (handlers?.onCardHoverEnd) {
    span.addEventListener('mouseleave', () => handlers.onCardHoverEnd());
  }
  return span;
}

// Atomic web-link pill. Same source-storage trick as card pills so
// the markdown round-trips; click opens the URL externally through
// Electron's setWindowOpenHandler.
function buildWebLink(text, url) {
  const span = document.createElement('span');
  span.className = 'md-web-link';
  span.contentEditable = 'false';
  span.dataset.url = url;
  span.dataset.cardSource = `[${text}](${url})`;
  span.title = url;
  span.textContent = text;
  span.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    openExternalUrl(url);
  });
  return span;
}

function appendInline(parent, text, handlers) {
  let remaining = text;
  while (remaining.length > 0) {
    const linkMatch = remaining.match(LINK_PATTERN);
    const bold = remaining.match(/\*\*([^*\n]+?)\*\*/);
    const code = remaining.match(/`([^`\n]+?)`/);
    const italic = remaining.match(/(^|[^*])\*([^*\s\n][^*\n]*?)\*(?!\*)/);
    const elementMatch = remaining.match(ELEMENT_WORD_PATTERN);
    const bareUrl = detectBareUrl(remaining);

    const candidates = [];
    if (linkMatch) {
      const url = linkMatch[2];
      if (isCardLinkUrl(url)) {
        candidates.push({
          type: 'card',
          index: linkMatch.index,
          length: linkMatch[0].length,
          name: linkMatch[1],
          cardId: url.slice('card:'.length),
        });
      } else if (isWebLinkUrl(url)) {
        candidates.push({
          type: 'web',
          index: linkMatch.index,
          length: linkMatch[0].length,
          text: linkMatch[1],
          url,
        });
      }
    }
    if (bold) {
      candidates.push({ type: 'bold', index: bold.index, length: bold[0].length, inner: bold[1] });
    }
    if (code) {
      candidates.push({ type: 'code', index: code.index, length: code[0].length, inner: code[1] });
    }
    if (italic) {
      const leadingAnchor = !italic[0].startsWith('*');
      candidates.push({
        type: 'italic',
        index: leadingAnchor ? italic.index + 1 : italic.index,
        length: leadingAnchor ? italic[0].length - 1 : italic[0].length,
        inner: italic[2],
      });
    }
    if (elementMatch) {
      candidates.push({
        type: 'element',
        index: elementMatch.index,
        length: elementMatch[0].length,
        word: elementMatch[1],
        element: normalizeElement(elementMatch[1]),
      });
    }
    if (bareUrl) {
      candidates.push({
        type: 'web',
        index: bareUrl.index,
        length: bareUrl.url.length,
        text: bareUrl.url,
        url: bareUrl.url,
      });
    }
    candidates.sort((a, b) => a.index - b.index);

    if (candidates.length === 0) {
      parent.appendChild(document.createTextNode(remaining));
      return;
    }

    const m = candidates[0];
    if (m.index > 0) {
      parent.appendChild(document.createTextNode(remaining.slice(0, m.index)));
    }

    if (m.type === 'card') {
      parent.appendChild(buildCardLink(m.name, m.cardId, handlers));
    } else if (m.type === 'web') {
      parent.appendChild(buildWebLink(m.text, m.url));
    } else if (m.type === 'element') {
      const span = document.createElement('span');
      span.className = `md-element md-element-${m.element.toLowerCase()}`;
      span.dataset.element = m.element;
      const icon = document.createElement('img');
      icon.className = 'md-element-icon';
      icon.alt = '';
      icon.src = `/game-assets/gameboard-tokens/${m.element.toLowerCase()}-token.svg`;
      icon.draggable = false;
      span.appendChild(icon);
      span.appendChild(document.createTextNode(m.word));
      parent.appendChild(span);
    } else {
      const tag = m.type === 'bold' ? 'strong' : m.type === 'italic' ? 'em' : 'code';
      const cls = m.type === 'bold' ? 'md-strong' : m.type === 'italic' ? 'md-em' : 'md-code';
      const marker = m.type === 'bold' ? '**' : m.type === 'italic' ? '*' : '`';
      const wrapper = document.createElement(tag);
      wrapper.className = cls;
      wrapper.appendChild(buildMarker(marker));
      wrapper.appendChild(document.createTextNode(m.inner));
      wrapper.appendChild(buildMarker(marker));
      parent.appendChild(wrapper);
    }

    remaining = remaining.slice(m.index + m.length);
  }
}
